import { Controller } from 'stimulus';

// Per-row controller on each feature_toggles index row. Intercepts the
// row's enable/disable form submission, posts as JSON, and updates the
// row's button + status badge in place from the server's response. The
// audit log is prepended via the page-level controller through a
// Stimulus outlet.
//
// HTML form fallback stays intact: if JS doesn't load (or this controller
// isn't mounted yet), the form submits normally and Rails redirects.
//
// Note: the legacy `inline_form_controller.js` precedent does
// `window.location.reload()` on success, defeating the no-reload goal.
// We explicitly do not. Server is authoritative for the new state and
// the rendered audit row; we apply both atomically without a reload.
export default class extends Controller {
  static targets = [
    'form',          // the <form>
    'submitButton',  // the Enable/Disable submit
    'enableField',   // hidden input that flips between 'true' and 'false'
    'reasonField',   // text input
    'statusBadge',   // ON/OFF span (in a different <td> from the form)
  ];

  static outlets = ['admin--feature-toggles--page'];

  submit(event) {
    event.preventDefault();
    if (this.busy) return;

    const form = this.formTarget;
    const formData = new FormData(form);
    formData.append('format', 'json');

    this.setBusy(true);

    window.Ajax.put(form.action, {
      data: formData,
      headers: [
        { key: 'Accept', value: 'application/json' },
        { key: 'X-Requested-With', value: 'XMLHttpRequest' },
      ],
      onSuccess: (responseText) => this.handleSuccess(responseText),
      onFail: (responseText) => this.handleFail(responseText),
      onDone: () => this.setBusy(false),
    });
  }

  handleSuccess(responseText) {
    let parsed;
    try { parsed = JSON.parse(responseText); } catch (e) { parsed = null; }
    if (!parsed || !parsed.success) {
      return this.handleFail(responseText);
    }

    this.applyToggleState(parsed.toggle);
    this.clearReason();

    if (parsed.audit_entry_html && this.hasAdminFeatureTogglesPageOutlet) {
      this.adminFeatureTogglesPageOutlet.prependAuditEntry(parsed.audit_entry_html);
    }

    if (parsed.message && window.Flash) {
      window.Flash.show('success', parsed.message);
    }
  }

  handleFail(responseText) {
    let parsed = null;
    try { parsed = JSON.parse(responseText); } catch (e) { parsed = null; }

    const message = (parsed && parsed.message) || 'Gagal mengubah toggle.';
    if (window.Flash) {
      window.Flash.show('danger', message);
    } else {
      console.error('[FeatureToggle]', message);
    }
  }

  // ---- helpers ----------------------------------------------------------

  setBusy(busy) {
    this.busy = busy;
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = busy;
    if (busy) {
      this.previousLabel = this.submitButtonTarget.value;
      this.submitButtonTarget.value = 'Memproses…';
      this.submitButtonTarget.setAttribute('aria-busy', 'true');
    } else {
      if (this.previousLabel != null) {
        // Only restore if applyToggleState hasn't already replaced it.
        if (this.submitButtonTarget.value === 'Memproses…') {
          this.submitButtonTarget.value = this.previousLabel;
        }
      }
      this.submitButtonTarget.removeAttribute('aria-busy');
    }
  }

  applyToggleState(toggle) {
    if (!toggle) return;

    // Hidden enable field — next click should send the *opposite* of the
    // new state. Server already computed `next_enable_value` for us.
    if (this.hasEnableFieldTarget && toggle.next_enable_value != null) {
      this.enableFieldTarget.value = toggle.next_enable_value;
    }

    // Submit button label + colour.
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.value = toggle.button_label;
      // Strip any prior btn-outline-* and reapply the new one.
      this.submitButtonTarget.classList.remove('btn-outline-success', 'btn-outline-danger');
      if (toggle.button_class) {
        this.submitButtonTarget.classList.add(toggle.button_class);
      }
    }

    // Status badge (ON/OFF pill). It lives in a sibling <td>, so we
    // scope the lookup to the closest row.
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = toggle.status_label;
      this.statusBadgeTarget.classList.remove('label-success', 'label-light');
      if (toggle.status_class) {
        this.statusBadgeTarget.classList.add(toggle.status_class);
      }
    }
  }

  clearReason() {
    if (this.hasReasonFieldTarget) {
      this.reasonFieldTarget.value = '';
    }
  }
}
